import { useState } from "react";
import {
  ArrowUpRight,
  FileText,
  ChevronDown,
  ChevronUp,
  ThumbsUp,
  ThumbsDown,
  Loader2,
  Check,
  X,
} from "lucide-react";
import type { DocumentRecord } from "../../types/record";
import { ScoreBadge } from "../ScoreBadge";
import { useFeedbackApi } from "../../lib/api/feedback";
import { useSearchParams } from "react-router-dom";
import { cn } from "../../lib/utils";

interface ResultCardProps {
  result: DocumentRecord;
}

export function ResultCard({ result }: ResultCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchParams] = useSearchParams();
  const feedbackApi = useFeedbackApi();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedFeedback, setSubmittedFeedback] = useState<
    "up" | "down" | null
  >(null);
  const [error, setError] = useState(false);
  const contentLength = result.content.length;
  const shouldTruncate = contentLength > 200;
  const truncatedContent =
    shouldTruncate && !isExpanded
      ? result.content.slice(0, 200) + "..."
      : result.content;

  const formatContent = (text: string) => {
    return text.split("\n").map((line, i) => (
      <span key={i}>
        {line}
        {i < text.split("\n").length - 1 && <br />}
      </span>
    ));
  };

  const handleFeedback = async (feedback: "up" | "down") => {
    try {
      setIsSubmitting(true);
      setError(false);
      const searchId = searchParams.get("search_id");
      if (!searchId) {
        console.error("No search ID found");
        setError(true);
        return;
      }

      await feedbackApi.createFeedback({
        search_id: parseInt(searchId),
        document_id: parseInt(result.id),
        is_positive: feedback === "up",
      });

      setSubmittedFeedback(feedback);
      console.log(
        `Document: ${result.id}, Score: ${result.score}, Title: ${
          result.title
        }, Feedback: ${feedback === "up" ? "good" : "bad"}`
      );
    } catch (error) {
      console.error("Failed to submit feedback:", error);
      setError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="group relative overflow-hidden rounded-xl border border-brand-border bg-brand-bg p-6 shadow-sm transition-all hover:shadow-card">
      <div className="absolute right-4 top-4 text-brand-border-strong transition-colors group-hover:text-brand-teal">
        <ArrowUpRight className="h-5 w-5" />
      </div>

      <div className="flex items-start gap-4">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-brand-primary to-brand-primary text-white">
          <FileText className="h-5 w-5" />
        </div>

        <div className="flex-1 text-left">
          <h3 className="font-semibold text-brand-text">{result.title}</h3>
          <p className="mt-2 text-sm leading-relaxed text-brand-muted whitespace-pre-line">
            {formatContent(truncatedContent)}
          </p>

          {shouldTruncate && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="mt-2 flex items-center gap-1 text-sm text-brand-teal hover:text-brand-primary"
            >
              {isExpanded ? (
                <>
                  Show less <ChevronUp className="h-4 w-4" />
                </>
              ) : (
                <>
                  Show more <ChevronDown className="h-4 w-4" />
                </>
              )}
            </button>
          )}

          <div className="mt-4 flex items-center justify-between">
            <ScoreBadge score={result.score || 0} />
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleFeedback("up")}
                disabled={isSubmitting || submittedFeedback !== null}
                className={cn(
                  "transition-colors",
                  submittedFeedback === "up"
                    ? "text-brand-teal"
                    : submittedFeedback === "down"
                    ? "text-brand-border-strong"
                    : "text-brand-teal hover:text-brand-teal",
                  (isSubmitting || submittedFeedback !== null) &&
                    "cursor-default"
                )}
              >
                {isSubmitting ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : submittedFeedback === "up" ? (
                  <Check className="h-5 w-5" />
                ) : (
                  <ThumbsUp className="h-5 w-5" />
                )}
              </button>
              <button
                onClick={() => handleFeedback("down")}
                disabled={isSubmitting || submittedFeedback !== null}
                className={cn(
                  "transition-colors",
                  submittedFeedback === "down"
                    ? "text-brand-error"
                    : submittedFeedback === "up"
                    ? "text-brand-border-strong"
                    : "text-brand-error hover:text-brand-error",
                  (isSubmitting || submittedFeedback !== null) &&
                    "cursor-default"
                )}
              >
                {isSubmitting ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : submittedFeedback === "down" ? (
                  <Check className="h-5 w-5" />
                ) : (
                  <ThumbsDown className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>
          {error && (
            <p className="mt-2 text-sm text-brand-error">
              Failed to submit feedback. Please try again.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
